import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { entries } from "@/mocks/entries";
import { siteConfig } from "@/mocks/site";
import EntryToc from "./components/EntryToc";
import EntryTimeline from "./components/EntryTimeline";
import RelatedEntries from "./components/RelatedEntries";

export default function EntryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const entry = entries.find((item) => item.slug === slug);

  const tocItems = useMemo(() => {
    if (!entry) return [];
    return [
      ...entry.sections.map((s) => ({ id: s.id, label: s.heading })),
      { id: "timeline", label: "事件时间线" },
      { id: "responses", label: "各方公开回应" },
      { id: "verdict", label: "理性看待" },
    ];
  }, [entry]);

  const jsonLd = useMemo(() => {
    if (!entry) return undefined;
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: entry.title,
        description: entry.summary,
        inLanguage: "zh-CN",
        datePublished: entry.publishedAt,
        dateModified: entry.updatedAt,
        articleSection: entry.categoryName,
        keywords: entry.tags.join(","),
        author: { "@type": "Organization", name: siteConfig.name },
        publisher: { "@type": "Organization", name: siteConfig.name },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/entry/${entry.slug}` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "事件词条库", item: `${origin}/entries` },
          { "@type": "ListItem", position: 3, name: entry.title, item: `${origin}/entry/${entry.slug}` },
        ],
      },
    ];
  }, [entry]);

  useSeo({
    title: entry ? `${entry.title} - 事件详解、背景与完整记录｜黑料百科` : "词条不存在 - 黑料百科",
    description: entry
      ? `${entry.summary} 本文按事件背景、时间线与各方回应完整记录${entry.title}的来龙去脉。`
      : "该词条不存在或已下线，返回事件词条库浏览其他瓜条。",
    keywords: entry ? `${entry.title},${entry.categoryName},事件详解,完整记录` : undefined,
    path: entry ? `/entry/${entry.slug}` : "/entries",
    jsonLd,
  });

  if (!entry) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-600">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">词条不存在或已下线</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              你访问的事件词条可能已被合并或归档。可以返回词条库，按话题重新查找。
            </p>
            <Link
              to="/entries"
              className="mt-7 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回事件词条库
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <article>
          <header className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-10 md:pb-14 md:pt-12">
            <Breadcrumbs
              items={[
                { label: "首页", href: "/" },
                { label: "事件词条库", href: "/entries" },
                { label: entry.categoryName, href: `/entries?cat=${entry.category}` },
                { label: entry.title },
              ]}
            />

            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-bookmark-3-line" />
                  {entry.categoryName}
                </span>
                <h1 className="mt-5 font-heading text-3xl font-black leading-[1.2] tracking-tight text-foreground-950 md:text-5xl">
                  {entry.title}
                </h1>
                <p className="mt-5 text-base leading-relaxed text-foreground-600 md:text-lg">{entry.lead}</p>

                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    首发 {entry.publishedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-refresh-line" />
                    更新 {entry.updatedAt}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    阅读约 {entry.readMinutes} 分钟
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" />
                    {entry.views.toLocaleString()}
                  </span>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {entry.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/entries?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="w-full overflow-hidden rounded-lg border border-background-200" style={{ height: "300px" }}>
                  <img
                    src={entry.cover}
                    alt={`${entry.title} — 事件背景与完整记录配图`}
                    title={`${entry.title} — 黑料百科事件词条`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 gap-10 px-4 py-12 md:px-10 md:py-16 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-8">
              {entry.sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-12 scroll-mt-28 last:mb-0">
                  <div className="flex items-baseline gap-3">
                    <span className="h-5 w-1 rounded-full bg-primary-500" />
                    <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                      {section.heading}
                    </h2>
                  </div>
                  <div className="mt-5 space-y-4">
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 12)} className="text-base leading-8 text-foreground-700">
                        {p}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <section id="timeline" className="mb-12 scroll-mt-28">
                <div className="flex items-baseline gap-3">
                  <span className="h-5 w-1 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    事件时间线
                  </h2>
                </div>
                <p className="mt-3 text-sm text-foreground-500">
                  按公开信息可确认的节点排序，标注每个转折发生的时间。
                </p>
                <div className="mt-7">
                  <EntryTimeline items={entry.timeline} />
                </div>
              </section>

              <section id="responses" className="mb-12 scroll-mt-28">
                <div className="flex items-baseline gap-3">
                  <span className="h-5 w-1 rounded-full bg-secondary-500" />
                  <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    各方公开回应
                  </h2>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {entry.responses.map((res) => (
                    <div
                      key={res.party}
                      className="rounded-lg border border-background-200 bg-background-100 p-5"
                    >
                      <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                        <i className="ri-megaphone-line text-secondary-600" />
                        {res.party}
                      </h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{res.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="verdict" className="scroll-mt-28">
                <div className="rounded-lg border-l-4 border-primary-500 bg-primary-50 p-6 md:p-8">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                    <i className="ri-scales-3-line text-primary-600" />
                    理性看待
                  </h2>
                  <p className="mt-4 text-base leading-8 text-foreground-700">{entry.verdict}</p>
                  <p className="mt-5 border-t border-primary-200 pt-4 text-xs text-foreground-500">
                    以上为基于公开信息的整理与观察，不代表对任何一方的定性。请理性讨论，拒绝人身攻击。
                  </p>
                </div>
              </section>
            </div>

            <div className="lg:col-span-4">
              <EntryToc items={tocItems} />
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">发现信息有误？</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  欢迎附上可查证的公开来源，我们核实后会更新词条并标注修改时间。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  提交更正
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </article>

        <RelatedEntries currentSlug={entry.slug} category={entry.category} />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}